import React, { useEffect, useState } from 'react';
import { Check, Link as LinkIcon, Linkedin, Twitter } from 'lucide-react';

/**
 * Share controls for a post. Set as a labelled row of rules rather than a
 * cluster of icon buttons, so it belongs to the page's typographic system.
 */
function SocialShare({ url, title, description = '' }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    {
      label: 'Twitter',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (err) {
      // Clipboard unavailable (insecure context or denied permission) — leave the
      // label alone rather than claiming a copy that did not happen.
      setCopied(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
      <span className="label">Share</span>

      {links.map(({ label, icon: Icon, href }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="link-rule inline-flex items-center gap-2 text-sm"
          title={description ? `${title} — ${description}` : title}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {label}
        </a>
      ))}

      <button
        type="button"
        onClick={copyLink}
        className="link-rule inline-flex items-center gap-2 text-sm"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-signal" aria-hidden="true" />
        ) : (
          <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        <span aria-live="polite">{copied ? 'Copied' : 'Copy link'}</span>
      </button>
    </div>
  );
}

export default SocialShare;
